import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { getApp } from "../app";
import { Type } from "../../../../shared/components";
import { Subscription } from "rxjs";
import { exportTypes, writeFileSpecialLogic } from "../export/export.service";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import { AlertData } from "../../../../shared/models/alert";
import {
  filterThreads,
  getDocsByIds,
  getAllImpactsVehicleName,
  getCoreTeamOptions,
  getTrainsCompressed,
  getWorkshopSupplierOptions
} from "../api.service";
import { Customer } from "../../../../shared/types/customers";
import { HomeMode } from "./home.service";
import { ActivatedRoute } from "@angular/router";
import { Z2DataMatch } from "../../../../shared/models/dataProviders/z2data";

@Component({
    selector: "app-home",
    templateUrl: "./home.component.html",
    styleUrls: ["./home.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class HomeComponent implements OnInit, OnDestroy {
  types: Type[] = ["thread", "post", "impact"];
  // loadedImageSubscription: Subscription = new Subscription();
  listReady = false;
  columns: string[] = [];
  filterOpen = false;
  exTypeTexts: string[] = [];

  private _routeSubscription: Subscription = new Subscription();

  app = getApp((app) => {
    this.app = app;
  });
  quickFilters: string[] = [];
  showFilters = false;

  constructor(private route: ActivatedRoute) {}

  async ngOnInit() {
    this.app.type = "thread";
    this.getSortOption();

    this._routeSubscription = this.route.data.subscribe((data) => {
      this.app.routing.setTabTitle(data.title);
    });

    this.getListOfColumns();
    if (
      this.app.auth.isUser &&
      this.app.customers.expectCurrent === Customer.DB
    ) {
      this.quickFilters = [
        "fav",
        "showAllCases",
        "personal",
        "deputy",
        "responsible"
      ];
    } else {
      this.quickFilters = this.app.list.thread.quickFilters;
    }
    await this.app.profile.getProfile(this.app.state.userId);
    /** Set omfNumber as active filter field before prepareCases so the search
     * input is visible immediately, without waiting for slow requests */
    this.app.filterList.field = this.app.fieldId.thread.omfNumber;
    await this.prepareCases();
    /** Restore omfNumber after prepareCases, since getThreadSearchResult resets
     * filterList.field to null. Only restore if user hasn't selected a different filter.
     * Use state.next directly to avoid the field setter forcing filterOpen to false */
    if (!this.app.filterList.field) {
      this.app.state.next({ filterField: this.app.fieldId.thread.omfNumber });
    }

    /** make the fields of type value - since they don't have know values on the homepage */
    // this.app.field.getFieldSettings("thread.crtNumber").type = "value";
    // this.app.field.getFieldSettings("thread.artNumber").type = "value";

    /** Used only to change the label for COOP client. To be removed when COOP will be on another branch */
    if (this.app.customers.expectCurrent === Customer.COOP) {
      this.app.thread.exportType = "All Anzeigen";
    } else {
      this.app.thread.exportType = "All Cases";
    }
    if (this.app.customers.expectCurrent === Customer.DB) {
      this.app.thread.coreTeams = await getCoreTeamOptions(
        this.app.customers.current
      );
      this.app.thread.workshopSuppliers = await getWorkshopSupplierOptions(
        this.app.customers.current
      );
    }
  }

  getListOfColumns() {
    this.app.list.thread.columns.forEach((column) => {
      this.columns.push(column.split(".")[1]);
    });
  }

  async prepareCases() {
    /** Restore the page the user was on before navigating into a case.
     * paginator.focus is preserved per view, so deriving the 1-based page from it
     * keeps the fetched data in sync with the pagination label after "back to list". */
    const page = (
      Math.floor(this.app.paginator.focus / this.app.paginator.pageSize) + 1
    ).toString();

    if (this.app.customers.expectCurrent === Customer.OMP) {
      this.app.state.next({
        showExternalCases: this.app.filterList.externalCasesDefault
      });
      await this.app.thread.getExternalCases(page);
      await this.app.applicationArea.getApplicationAreaFieldOptions();
    } else {
      if (this.app.state.filterByPage.length !== 0) {
        this.filterOpen = true;
        this.app.filterList.open = true;
        await this.app.thread.getThreadSearchResult(page);
      } else {
        await this.app.thread.getCasesCompressed(page);
      }
    }

    /** Re-apply only a real quick filter. "" and "showAllCases" have nothing to
     * restore, and going through addQuickFilter would close the filter panel
     * prepareCases just opened for the filters restored from filterByPage. */
    const quickFilter = this.app.filterFields.quickFilterSelected;
    if (quickFilter !== "" && quickFilter !== "showAllCases") {
      await this.app.filterFields.addQuickFilter(quickFilter, true);
    }

    /** created a subscription in order to be able to catch when the cases are set on this.app.docs.docs */
    this.app.docs.docsSubject.subscribe(async (value) => {
      if (value) {
        this.listReady = value;
        this.app.spinner.hideSpinner();
      }
    });

    // get list of users to be displayed in the filters dropdowns (non-blocking)
    this.app.users.getUsersWithRoles();
    this.app.users.getAllUserNames();

    if (this.app.customers.expectCurrent === Customer.NS) {
      this.app.train.trainsCompressed = await getTrainsCompressed(
        this.app.customers.expectCurrent
      );
    }
    getAllImpactsVehicleName(this.app.customers.expectCurrent).then(
      (names) => (this.app.impacts.impactVehicleNames = names)
    );
  }

  getPage() {
    const elements = this.app.paginator.getPage(this.app.thread.ids);
    return elements;
  }

  setFav(fav: boolean): void {
    this.app.thread.setFav(this.app.thread.thread._id, fav);
  }

  getSortOption() {
    const sortField = this.app.table.sortField;
    const index = this.app.list.thread.sortOptions.findIndex(
      (f) => f === sortField
    );
    if (index === -1 && this.app.home.homeMode === "list") {
      this.app.table.sortField = "thread.update_time";
      this.app.filterFields.quickFilterSelected = "showAllCases";
    }
  }

  getExportTypeText(type: string) {
    if (type === "ALL-Cases" || type === "All Cases") {
      return "ALL-Cases";
    } else {
      if ((exportTypes as any)[type]) {
        return (exportTypes as any)[type].text[this.app.language];
      }
    }
  }

  /** prepare data for exporting */

  async writeFile(type: string) {
    this.app.thread.exportType = type;
    this.app.spinner.showSpinner();

    if (!(await this.checkFiltersForExport())) {
      await this.app.thread.getAllThreads();
      this.app.thread.ids = (this.app.thread.threads as any[]).map(
        (t: any) => t._id
      );
    }

    if (this.app.customers.expectCurrent === Customer.DB) {
      await this.app.thread.setCommodityIdOnThread();
    }
    await writeFileSpecialLogic(this.app, type);
    this.app.mail.updateUnreadCount.next(true);
    this.app.spinner.hideSpinner();
  }

  /** Checks if filters are active and loads only the matching threads for export.
   *  Returns true if filters were applied, false if the caller should fall back to getAllThreads. */
  private async checkFiltersForExport(): Promise<boolean> {
    const quickFilter = this.app.filterFields.quickFilterSelected;
    const serverFilters = this.app.state.filterByPage
      .filter(
        (f) =>
          f.filterLabelField &&
          !this.app.filterFields.isQuickFilterField(f.filterLabelField)
      )
      .map((f) => ({
        ...f,
        values: f.values instanceof Set ? Array.from(f.values) : f.values
      }));
    const hasQuickFilter =
      quickFilter && quickFilter !== "" && quickFilter !== "showAllCases";

    if (!hasQuickFilter && serverFilters.length === 0) {
      return false;
    }

    const quickFilterData = hasQuickFilter
      ? await this.app.filterFields.getQuickFilterData(quickFilter)
      : undefined;
    const response = await filterThreads(
      this.app.customers.expectCurrent,
      serverFilters,
      undefined,
      undefined,
      hasQuickFilter ? quickFilter : undefined,
      quickFilterData,
      this.app.state.sortDirection,
      this.app.state.sortField,
      false
    );
    this.app.thread.ids = response.ids;
    this.app.thread.threads = await getDocsByIds(
      this.app.customers.expectCurrent,
      "thread",
      response.ids
    );
    return true;
  }

  navigateNewThread() {
    // clear all variables that could have fields info about a thread
    this.app.sync.clearTheSyncInfo();
    this.app.pcn.pcn = null;
    this.app.pcn.id = null;
    this.app.treeRow.currentManufacturer = new Manufacturer();
    this.app.alerts.currentSelected = {} as AlertData;
    this.app.externalDataFilter.selectedMatch = new Z2DataMatch();
    sessionStorage.removeItem("fromTree");
    this.app.routing.navigateNewThread();
  }

  async setHomeMode(mode: HomeMode) {
    this.app.home.homeMode = mode;
    this.getSortOption();
    if (this.app.customers.expectCurrent === Customer.NS) {
      this.app.spinner.showSpinner();
      const quickFilter = this.app.filterFields.quickFilterSelected;
      const hasActiveFilter =
        this.app.state.filterByPage.length > 0 ||
        (quickFilter !== "" && quickFilter !== "showAllCases");
      if (hasActiveFilter) {
        await this.app.thread.getThreadSearchResult("1");
      } else {
        if (mode === "table") {
          this.app.state.next({
            sortField: "thread.omfStatus",
            sortDirection: 1
          });
        }
        await this.app.thread.getCasesCompressed();
      }
      this.app.spinner.hideSpinner();
    }
  }

  async toggleSort(field: string) {
    if (this.app.table.sortField === field) {
      // toggle direction
      this.app.table.sortDirection =
        this.app.table.sortDirection === -1 ? 1 : -1;
    } else {
      // new field -> default Newest first
      this.app.table.sortField = field;
      this.app.table.sortDirection = -1;
    }

    this.app.spinner.showSpinner();
    if (this.app.state.filterByPage.length > 0) {
      await this.app.thread.getThreadSearchResult("1");
    } else {
      await this.app.thread.getCasesCompressed("1");
    }
    this.app.spinner.hideSpinner();
  }

  ngOnDestroy(): void {
    // if (this.loadedImageSubscription) {
    //   this.loadedImageSubscription.unsubscribe();
    // }
    if (this._routeSubscription) {
      this._routeSubscription.unsubscribe();
    }
  }
}
